import React, { useEffect, useState } from 'react';
import { useAppointmentTypes } from '@features/appointment-types';
import {
  type Appointment as AppointmentResponse,
  AppointmentFormFields,
  type AppointmentUpdate,
  CustomRecurrenceModal,
  DirtyChangesAlert,
  NoShowAction,
  PatientConfirmedStatus,
  PRESET_DURATIONS,
  useAppointmentFormReset,
  useAppointmentFormValidation,
  useAppointmentSubmission,
  useDirtyStateTracking,
  useDurationSchedule,
  useFormsAndReminders,
  useOrgScheduleAccess,
  useOrgScheduleFilters,
  useReassignAppointmentMutation,
  useRecurrence,
  useTelehealth,
} from '@features/appointments';
import { EligibilitySummaryContent } from '@features/eligibility-checks';
import { useOrganizationId, useTemplatesList } from '@features/user';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DeleteIcon from '@mui/icons-material/Delete';
import { Alert, Box, Button, CircularProgress, Stack, Tooltip, Typography } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { add, format } from 'date-fns';
import {
  getModalityPlaceOfServiceError,
  getTelehealthForModality,
  normalizeCptCodes,
  normalizeVisitModality,
  type ConcreteVisitModality,
  type VisitModality,
} from '@utils/modalityPlaceOfService';
import { getSessionBillingCptCodes, getSessionBillingPlaceOfService } from '@utils/sessionBilling';

import { DeleteConfirmDialog } from '../../../../components/DeleteConfirmDialog';
import { type Patient } from '../../../../types/types';

function AppointmentEligibilitySection({
  patientId,
  encounterId,
  patientName,
}: {
  patientId?: string;
  encounterId?: string;
  patientName?: string;
}) {
  return (
    <Box
      sx={{
        mt: 3,
        p: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <EligibilitySummaryContent
        patientId={patientId}
        encounterId={encounterId}
        patientName={patientName}
        source="edit_appointment"
      />
    </Box>
  );
}

interface ContentProps {
  appointment: AppointmentResponse;
  onAppointmentUpdate: (
    appointmentId: string,
    updates: AppointmentUpdate,
    seriesId?: string,
  ) => Promise<void>;
  onAppointmentDelete?: (appointmentId: string, seriesId?: string) => Promise<void>;
  handleRecordClick: () => Promise<void>;
}

export const Content: React.FC<ContentProps> = ({
  appointment,
  onAppointmentUpdate,
  onAppointmentDelete,
  handleRecordClick,
}) => {
  const templatesList = useTemplatesList();
  const { appointment: appointmentTracking, orgSchedule: orgScheduleTracking } = useEventTracking();
  const [reassignAppointment] = useReassignAppointmentMutation();

  // Org schedule admin state
  const { isOrgViewActive } = useOrgScheduleFilters();
  const { hasOrgScheduleAccess } = useOrgScheduleAccess();
  const isOrgScheduleActive = hasOrgScheduleAccess && isOrgViewActive;
  const appointmentProviderId =
    appointment.provider_id ?? appointment.provider?.user_id ?? undefined;
  const [selectedProviderId, setSelectedProviderId] = useState<string | undefined>(
    appointmentProviderId,
  );

  // Fetch all appointment types to find the matching one (use selected provider in org mode)
  const { appointmentTypes, isLoading: isAppointmentTypesLoading } = useAppointmentTypes(
    undefined,
    isOrgScheduleActive ? selectedProviderId : undefined,
  );

  // Patient selection state
  const [selectedPatient, setSelectedPatient] = useState<Patient | undefined>(undefined);

  // Notes state
  const [notes, setNotes] = useState(appointment.notes ?? '');

  // Labels state (edit-mode override of encounter-type defaults)
  const [labelIds, setLabelIds] = useState<string[]>(
    appointment.labels?.map(label => label.label_id) ?? [],
  );
  const userOrgId = useOrganizationId();
  const organizationId = appointment.organization_id ?? userOrgId ?? undefined;

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Initialize modular hooks
  const durationSchedule = useDurationSchedule();

  const recurrence = useRecurrence();

  const telehealth = useTelehealth({
    initialTelehealth: appointment.telehealth ?? false,
  });
  const [modality, setModality] = useState<VisitModality>(
    normalizeVisitModality(appointment.modality) ?? (appointment.telehealth ? 'video' : null),
  );
  const [placeOfService, setPlaceOfService] = useState<string | null>(
    getSessionBillingPlaceOfService(appointment),
  );
  const [cptCodes, setCptCodes] = useState<string[]>(getSessionBillingCptCodes(appointment));
  const [useJotmeet, setUseJotmeet] = useState(Boolean(appointment.use_jotmeet));

  // Initialize dirty tracking first without dependencies
  const dirtyTrackingRef = React.useRef<any>(null);

  const formsAndReminders = useFormsAndReminders({
    initialAppointment: appointment,
    // templateId: templatesList?.[0]?.template_id || null,
    onMarkDirty: field => dirtyTrackingRef.current?.markDirty(field),
  });

  const dirtyTracking = useDirtyStateTracking({
    selectedAppointmentType: formsAndReminders.selectedAppointmentType,
    selectedDuration: durationSchedule.selectedDuration,
    currentTelehealth: telehealth.telehealth,
    currentModality: modality,
    currentPlaceOfService: placeOfService,
    currentCptCodes: cptCodes,
    currentUseJotmeet: useJotmeet,
    currentForms: formsAndReminders.selectedForms,
    currentReminders: formsAndReminders.selectedReminders,
  });

  // Update ref
  dirtyTrackingRef.current = dirtyTracking;

  // Track if initial appointment data has been loaded
  const [hasLoadedAppointmentData, setHasLoadedAppointmentData] = useState(false);

  // Auto-populate defaults from appointment type when not dirty
  useEffect(() => {
    if (!formsAndReminders.selectedAppointmentType) return;

    // Don't auto-populate if we're overriding defaults
    if (appointment.override_defaults) return;

    // Don't auto-populate if we have not loaded appointment data yet
    if (!hasLoadedAppointmentData) return;

    // Auto-populate duration if not dirty and default exists
    if (
      !dirtyTracking.isDirty('duration') &&
      formsAndReminders.selectedAppointmentType.default_duration
    ) {
      durationSchedule.handleDurationSelect(
        formsAndReminders.selectedAppointmentType.default_duration,
      );
    }

    // Auto-populate forms if not dirty
    if (!dirtyTracking.isDirty('forms')) {
      formsAndReminders.populateFormsFromDefaults(formsAndReminders.selectedAppointmentType);
    }

    // Auto-populate reminders if not dirty
    if (!dirtyTracking.isDirty('reminders')) {
      formsAndReminders.populateRemindersFromDefaults(formsAndReminders.selectedAppointmentType);
    }

    // Auto-populate telehealth if not dirty
    if (
      !dirtyTracking.isDirty('telehealth') &&
      formsAndReminders.selectedAppointmentType.telehealth !== undefined
    ) {
      telehealth.setTelehealth(formsAndReminders.selectedAppointmentType.telehealth ?? false);
    }

    if (!dirtyTracking.isDirty('session')) {
      const defaultModality =
        formsAndReminders.selectedAppointmentType.default_modality ??
        (formsAndReminders.selectedAppointmentType.telehealth ? 'video' : null);
      setModality(defaultModality);
      telehealth.setTelehealth(getTelehealthForModality(defaultModality));
      setPlaceOfService(formsAndReminders.selectedAppointmentType.default_place_of_service ?? null);
      setCptCodes(normalizeCptCodes(formsAndReminders.selectedAppointmentType.default_cpt_codes));
      setUseJotmeet(
        Boolean(
          formsAndReminders.selectedAppointmentType.default_use_jotmeet &&
          defaultModality === 'video',
        ),
      );
    }
  }, [
    formsAndReminders.selectedAppointmentType,
    hasLoadedAppointmentData,
    appointment.override_defaults,
  ]);

  // Re-select appointment type when provider's types change (org admin switching providers)
  useEffect(() => {
    if (!appointmentTypes || appointmentTypes.length === 0 || !hasLoadedAppointmentData) return;

    const currentType = formsAndReminders.selectedAppointmentType;

    // No type selected (cleared by provider switch) → auto-select first
    if (!currentType) {
      formsAndReminders.handleAppointmentTypeSelect(appointmentTypes[0]);
      return;
    }

    // Current type not in the new list → select first from new list
    const stillValid = appointmentTypes.some(
      t => t.encounter_type_id === currentType.encounter_type_id,
    );
    if (!stillValid) {
      formsAndReminders.handleAppointmentTypeSelect(appointmentTypes[0]);
    }
  }, [appointmentTypes, formsAndReminders.selectedAppointmentType]);

  // Form validation
  const validation = useAppointmentFormValidation({
    selectedPatient,
    selectedAppointmentType: formsAndReminders.selectedAppointmentType,
    durationSchedule,
    mode: 'edit',
  });

  // Form submission
  const submission = useAppointmentSubmission({
    mode: 'edit',
    selectedPatient,
    selectedAppointmentType: formsAndReminders.selectedAppointmentType,
    durationSchedule,
    recurrence,
    templateId: templatesList?.[0]?.template_id || null,
    selectedForms: formsAndReminders.selectedForms,
    selectedReminders: formsAndReminders.selectedReminders,
    telehealth: telehealth.telehealth,
    modality,
    placeOfService,
    cptCodes,
    useJotmeet,
    notes,
    labelIds,
    hasDirtyChanges: dirtyTracking.showDirtyAlert && !appointment.override_defaults,
    providerId: isOrgScheduleActive ? selectedProviderId : undefined,
    appointment,
    onAppointmentUpdate,
  });

  // Form reset
  const { resetForm: resetFormData } = useAppointmentFormReset({
    durationSchedule,
    recurrence,
    dirtyTracking,
    onPatientReset: () => setSelectedPatient(undefined),
  });

  // Coordinate between hooks - duration changes mark as dirty
  const handleDurationChange = (duration: number) => {
    durationSchedule.handleDurationSelect(duration);
    dirtyTracking.markDirty('duration');
  };

  // Coordinate between hooks - telehealth changes mark as dirty
  const handleTelehealthChange = (checked: boolean) => {
    telehealth.handleTelehealthChange(checked);
    setModality(checked ? 'video' : 'in_person');
    if (!checked) {
      setUseJotmeet(false);
    }
    dirtyTracking.markDirty('telehealth');
    dirtyTracking.markDirty('session');
  };

  const handleModalityChange = (nextModality: ConcreteVisitModality | null) => {
    setModality(nextModality);
    telehealth.setTelehealth(getTelehealthForModality(nextModality));
    if (nextModality !== 'video') {
      setUseJotmeet(false);
    }
    dirtyTracking.markDirty('session');
  };

  const handlePlaceOfServiceChange = (nextPlaceOfService: string | null) => {
    setPlaceOfService(nextPlaceOfService);
    dirtyTracking.markDirty('session');
  };

  const handleCptCodesChange = (nextCptCodes: string[]) => {
    setCptCodes(normalizeCptCodes(nextCptCodes));
    dirtyTracking.markDirty('session');
  };

  const handleUseJotmeetChange = (nextUseJotmeet: boolean) => {
    setUseJotmeet(nextUseJotmeet);
    dirtyTracking.markDirty('session');
  };

  // Handle provider selection (org schedule admin)
  const handleProviderSelect = (providerId: string) => {
    setSelectedProviderId(providerId);
    // Reset appointment type since types are per-provider
    formsAndReminders.handleAppointmentTypeSelect(undefined);
  };

  // Handler for patient selection
  const handlePatientSelect = (patient: Patient | undefined) => {
    setSelectedPatient(patient);
  };

  const handleSaveAppointment = async () => {
    const sessionError = getModalityPlaceOfServiceError({
      modality,
      placeOfService,
    });
    if (sessionError) {
      submission.setError(sessionError);
      return;
    }

    if (!validation.isFormValid) {
      submission.setError(validation.getValidationMessage());
      return;
    }

    await submission.submit();

    if (!submission.error) {
      const providerChanged =
        isOrgScheduleActive && selectedProviderId !== (appointment.provider_id ?? undefined);

      if (providerChanged) {
        try {
          await reassignAppointment({
            appointmentId: appointment.appointment_id,
            appointmentReassign: { provider_id: selectedProviderId ?? null },
          }).unwrap();

          orgScheduleTracking.providerReassigned({
            appointment_id: appointment.appointment_id,
            previous_provider_id: appointment.provider_id ?? null,
            new_provider_id: selectedProviderId ?? null,
          });
        } catch (error) {
          console.error('Failed to reassign appointment provider:', error);
          submission.setError('Failed to reassign provider. Please try again.');
          return;
        }
      }

      appointmentTracking.update(appointment.appointment_id, {
        is_org_schedule: isOrgScheduleActive,
        target_provider_id: isOrgScheduleActive ? selectedProviderId : undefined,
      });

      // Track note changes
      const originalNotes = appointment.notes ?? '';
      if (notes !== originalNotes) {
        appointmentTracking.noteUpdated({
          appointment_id: appointment.appointment_id,
          note_length: notes.trim().length,
          is_org_schedule: isOrgScheduleActive,
          target_provider_id: isOrgScheduleActive ? selectedProviderId : undefined,
        });
      }
    }
  };

  const handleDeleteAppointment = async () => {
    if (!appointment?.appointment_id) return;

    setIsDeleting(true);
    try {
      // Check if this is a recurring appointment
      if (appointment.series && onAppointmentDelete) {
        // Use the provided handler for recurring appointments
        await onAppointmentDelete(appointment.appointment_id, appointment.series.series_id);
      } else if (onAppointmentDelete) {
        // Single appointment deletion
        await onAppointmentDelete(appointment.appointment_id, undefined);
      } else {
        // Fallback if no handler provided (shouldn't happen)
        console.error('No onAppointmentDelete handler provided');
        submission.setError('Unable to delete appointment. Please try again.');
      }
    } catch (err) {
      console.error('Error deleting appointment:', err);
      submission.setError('Failed to delete appointment. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle dirty alert actions
  const handleUpdateAppointmentType = async () => {
    try {
      await formsAndReminders.handleUpdateAppointmentType(
        durationSchedule.selectedDuration,
        telehealth.telehealth,
        {
          modality,
          placeOfService,
          cptCodes,
          useJotmeet,
        },
      );
      dirtyTracking.resetDirtyState();
    } catch (error) {
      console.error('Failed to update appointment type:', error);
      submission.setError(
        error instanceof Error && error.message
          ? error.message
          : 'Failed to update appointment type. Please try again.',
      );
    }
  };

  // Initialize appointment data when component mounts or appointment changes
  useEffect(() => {
    if (!appointment) return;
    submission.setError(null);

    // Find and set the appointment type
    if (appointment.encounter_type_id && appointmentTypes) {
      const matchingType = appointmentTypes.find(
        type => type.encounter_type_id === appointment.encounter_type_id,
      );
      if (matchingType) {
        formsAndReminders.handleAppointmentTypeSelect(matchingType);
      }
    }

    // Populate forms and reminders from appointment data
    formsAndReminders.populateFormsFromAppointment(appointment);
    formsAndReminders.populateRemindersFromAppointment(appointment);

    if (appointment.start_time) {
      // Set initial date and time values
      const scheduledDate = new Date(appointment.start_time);
      durationSchedule.setDate(format(scheduledDate, 'yyyy-MM-dd'));
      durationSchedule.setStartTime(format(scheduledDate, 'HH:mm'));

      // Set duration
      if (appointment.duration) {
        durationSchedule.setSelectedDuration(appointment.duration);

        // Check if it's a preset duration
        const isPresetDuration = PRESET_DURATIONS.some(
          preset => preset.minutes === appointment.duration,
        );

        durationSchedule.setIsCustomDuration(!isPresetDuration);

        // Calculate end time based on duration
        const endDateTime = add(scheduledDate, {
          minutes: appointment.duration,
        });
        durationSchedule.setEndTime(format(endDateTime, 'HH:mm'));
      } else {
        // Default duration if not set
        durationSchedule.setSelectedDuration(30);
        durationSchedule.setIsCustomDuration(false);

        // Calculate default end time
        const endDateTime = add(scheduledDate, { minutes: 30 });
        durationSchedule.setEndTime(format(endDateTime, 'HH:mm'));
      }

      // Parse series.rrule if it exists
      if (appointment.series?.rrule) {
        recurrence.parseRRule(appointment.series.rrule);
      } else {
        recurrence.setRecurrence('none');
      }
    }

    // Set initial patient if available
    if (appointment.patient) {
      // Convert PatientInfo to Patient format
      const patientData: Patient = {
        patient_id: appointment.patient.patient_id,
        first_name: appointment.patient.name?.split(' ')[0] || '',
        last_name: appointment.patient.name?.split(' ').slice(1).join(' ') || '',
        dob: appointment.patient.dob || undefined,
        phone: appointment.patient.telephone || undefined,
        identifier: appointment.patient.identifier || undefined,
      };
      setSelectedPatient(patientData);
    } else {
      setSelectedPatient(undefined);
    }

    // Set telehealth from appointment data if available
    if (appointment.telehealth !== undefined && appointment.telehealth !== null) {
      telehealth.setTelehealth(appointment.telehealth);
    }

    setModality(
      normalizeVisitModality(appointment.modality) ?? (appointment.telehealth ? 'video' : null),
    );
    setPlaceOfService(getSessionBillingPlaceOfService(appointment));
    setCptCodes(getSessionBillingCptCodes(appointment));
    setUseJotmeet(Boolean(appointment.use_jotmeet));

    // Set notes from appointment data
    setNotes(appointment.notes ?? '');

    // Set labels from appointment data
    setLabelIds(appointment.labels?.map(label => label.label_id) ?? []);

    // Mark that we've loaded the appointment data
    setHasLoadedAppointmentData(true);

    return () => {
      // Reset component state when unmounting
      resetFormData();
      setHasLoadedAppointmentData(false);
    };
  }, [appointment?.appointment_id]); // Only re-run when appointment ID changes, not when appointmentTypes updates

  if (!appointment || isAppointmentTypesLoading) {
    return null;
  }

  const sessionError = getModalityPlaceOfServiceError({
    modality,
    placeOfService,
  });

  // Error state
  if (submission.error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {submission.error}
      </Alert>
    );
  }

  // Default state (appointment exists, no error)
  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          padding: 0,
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflow: 'scroll',
            pb: 4,
            pt: 1,
          }}
        >
          {/* Suggested Appointment Banner */}
          {appointment.status === 'suggested' && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 4,
              }}
            >
              <AutoAwesomeIcon sx={{ color: 'primary.main', fontSize: 20 }} />
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                Suggested Appointment
              </Typography>
            </Box>
          )}

          <AppointmentFormFields
            // Provider (org schedule admin)
            showProviderPicker={isOrgScheduleActive}
            selectedProviderId={selectedProviderId}
            onProviderSelect={handleProviderSelect}
            providerIdForTypes={isOrgScheduleActive ? selectedProviderId : undefined}
            // Patient
            selectedPatient={selectedPatient}
            onPatientSelect={handlePatientSelect}
            // Appointment Type
            selectedAppointmentType={formsAndReminders.selectedAppointmentType}
            onAppointmentTypeSelect={formsAndReminders.handleAppointmentTypeSelect}
            // Duration Schedule
            date={durationSchedule.date}
            startTime={durationSchedule.startTime}
            endTime={durationSchedule.endTime}
            selectedDuration={durationSchedule.selectedDuration}
            isCustomDuration={durationSchedule.isCustomDuration}
            onDateChange={durationSchedule.handleDateChange}
            onStartTimeChange={durationSchedule.handleStartTimeChange}
            onEndTimeChange={durationSchedule.handleEndTimeChange}
            onDurationSelect={handleDurationChange}
            onCustomDurationSelect={durationSchedule.handleCustomDuration}
            // Recurrence
            recurrence={recurrence.recurrence}
            customRecurrenceRule={recurrence.customRecurrenceRule}
            onRecurrenceChange={recurrence.handleRecurrenceChange}
            onCustomRecurrenceClick={() => recurrence.setIsCustomRecurrenceModalOpen(true)}
            getRecurrenceText={recurrence.getRecurrenceText}
            // Telehealth
            telehealth={telehealth.telehealth}
            onTelehealthChange={handleTelehealthChange}
            modality={modality}
            onModalityChange={handleModalityChange}
            placeOfService={placeOfService}
            onPlaceOfServiceChange={handlePlaceOfServiceChange}
            cptCodes={cptCodes}
            onCptCodesChange={handleCptCodesChange}
            useJotmeet={useJotmeet}
            onUseJotmeetChange={handleUseJotmeetChange}
            sessionError={sessionError}
            // Forms and Reminders
            selectedForms={formsAndReminders.selectedForms}
            selectedReminders={formsAndReminders.selectedReminders}
            onAddForm={formsAndReminders.handleAddForm}
            onUpdateForm={formsAndReminders.handleUpdateForm}
            onRemoveForm={formsAndReminders.handleRemoveForm}
            onAddReminder={formsAndReminders.handleAddReminder}
            onUpdateReminder={formsAndReminders.handleUpdateReminder}
            onRemoveReminder={formsAndReminders.handleRemoveReminder}
            // Notes
            notes={notes}
            onNotesChange={setNotes}
            // Labels
            labelIds={labelIds}
            onLabelIdsChange={setLabelIds}
            organizationId={organizationId}
            // Mode
            mode="view"
            showRecurrence={!appointment.series}
            // Action links
            actionLinks={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <PatientConfirmedStatus appointment={appointment} />
                <NoShowAction
                  appointment={appointment}
                  onMarkNoShowClick={() => {
                    appointmentTracking.actionClicked({
                      appointment_id: appointment.appointment_id,
                      action: 'mark_no_show',
                      source: 'appointment_edit',
                      is_org_schedule: isOrgScheduleActive,
                    });
                  }}
                />
              </Box>
            }
          />

          {/* Eligibility Info */}
          <AppointmentEligibilitySection
            patientId={appointment.patient?.patient_id}
            encounterId={appointment.encounter?.encounter_id}
            patientName={
              appointment.patient
                ? [appointment.patient.first_name, appointment.patient.last_name]
                    .filter(Boolean)
                    .join(' ')
                : undefined
            }
          />
        </Box>
        <Box
          sx={{
            mx: -2,
            p: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            position: 'sticky',
            bottom: 0,
            background: 'background.paper',
            zIndex: 1,
          }}
        >
          <DirtyChangesAlert
            show={dirtyTracking.showDirtyAlert && !appointment.override_defaults}
            appointmentTypeName={formsAndReminders.selectedAppointmentType?.encounter_type || ''}
            changes={dirtyTracking.changes}
            selectedAppointmentType={formsAndReminders.selectedAppointmentType}
            currentDuration={durationSchedule.selectedDuration}
            currentTelehealth={telehealth.telehealth}
            currentModality={modality}
            currentPlaceOfService={placeOfService}
            currentCptCodes={cptCodes}
            currentUseJotmeet={useJotmeet}
            currentForms={formsAndReminders.selectedForms}
            currentReminders={formsAndReminders.selectedReminders}
            currentTemplate={
              templatesList?.find(
                t => t.template_id === formsAndReminders.selectedAppointmentType?.template_id,
              ) || null
            }
            onUpdateAppointmentType={handleUpdateAppointmentType}
          />

          <Stack direction="row" spacing={2} sx={{ justifyContent: 'space-between' }}>
            <Button
              variant="contained"
              onClick={() => {
                // Track the record session action
                appointmentTracking.actionClicked({
                  appointment_id: appointment.appointment_id,
                  action: 'record_session',
                  source: 'appointment_edit',
                  is_org_schedule: isOrgScheduleActive,
                });
                handleRecordClick();
              }}
              color="secondary"
              fullWidth
              sx={{
                maxWidth: 300,
              }}
            >
              {appointment?.encounter?.status === 'completed' ||
              appointment?.encounter?.status === 'processing'
                ? 'Go to Note'
                : 'Start Recording'}
            </Button>
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                startIcon={isDeleting ? <CircularProgress size={16} /> : <DeleteIcon />}
                onClick={() => setIsDeleteDialogOpen(true)}
                disabled={submission.isSubmitting || isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </Button>
              <Tooltip
                title={
                  !validation.isFormValid
                    ? validation.getValidationMessage()
                    : sessionError
                      ? sessionError
                      : ''
                }
                placement="top"
              >
                <span>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSaveAppointment}
                    disabled={
                      submission.isSubmitting ||
                      !validation.isFormValid ||
                      Boolean(sessionError) ||
                      isDeleting
                    }
                  >
                    {submission.isSubmitting ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      'Save'
                    )}
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
        </Box>
      </Box>
      <DeleteConfirmDialog
        open={isDeleteDialogOpen}
        type="appointment"
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={() => {
          setIsDeleteDialogOpen(false);
          handleDeleteAppointment();
        }}
      />

      <CustomRecurrenceModal
        open={recurrence.isCustomRecurrenceModalOpen}
        onClose={() => recurrence.setIsCustomRecurrenceModalOpen(false)}
        onSave={recurrence.handleCustomRecurrenceSave}
        initialRule={recurrence.customRecurrenceRule || undefined}
      />
    </>
  );
};
